/**
 * Shared, security-hardened CSV cell primitives. The Echo audit-log exporter,
 * the scheduled analytics-report deliverer, and the browser's on-screen export
 * all emit CSVs from attacker-influenced data, so the formula-injection +
 * RFC-4180 rules MUST live in exactly one place. This module is that place.
 *
 * It lives in `@nexus/types` rather than in either app because the frontend and
 * the backend both produce CSVs of the same rows: a copy on one side would be a
 * second implementation of a security rule, free to drift out of agreement with
 * the first without anything failing.
 *
 * Node-free by construction — no `Buffer`, no `fs`. Callers wrap the string in
 * whatever their platform hands to a user (`Buffer` on the server, `Blob` in the
 * browser).
 */

/**
 * Leading bytes that Excel, Google Sheets, LibreOffice Calc, and Numbers
 * interpret as the start of a formula when the CSV is opened directly
 * (CWE-1236 — CSV Formula Injection). Exported data carries attacker-influenced
 * values (resource names, descriptions, grouped dimension values), so these
 * cells MUST be neutralised before any spreadsheet sees them — otherwise a
 * crafted value like `=IMPORTXML(...)` runs when a reviewer opens the CSV.
 *
 *   =   formula (all vendors)
 *   +   formula (all vendors)
 *   -   formula / unary (all vendors)
 *   @   Excel formula operator
 *   \t  Excel consumes the tab and parses what follows as a formula
 *   \r  Excel consumes the CR and parses what follows as a formula
 *
 * Mitigation: prefix the cell with a single quote. Spreadsheets strip the
 * leading quote when rendering and force the cell to text. Plain CSV parsers
 * see it as a literal quote, which is the correct behaviour (the raw value
 * isn't a formula either).
 */
const FORMULA_INJECTION_PREFIXES = new Set<string>(["=", "+", "-", "@", "\t", "\r"]);

export function neutraliseFormulaInjection(value: string): string {
  // One read, not two: an absent first character IS the empty string, so
  // deriving the empty-value guard from the same lookup the rule needs keeps
  // them from ever disagreeing. (It also satisfies `noUncheckedIndexedAccess`,
  // which this file never met while it lived behind the backend's looser
  // tsconfig — the frontend compiles it now.)
  const first = value[0];
  if (first === undefined) return value;
  return FORMULA_INJECTION_PREFIXES.has(first) ? `'${value}` : value;
}

/**
 * CSV cell escape per RFC 4180: wrap in double quotes if the value contains a
 * comma, double quote, or newline. Double quotes inside the value are doubled.
 *
 * Formula-injection neutralisation runs FIRST so the `'`-prefixed form is what
 * gets RFC-4180 quoted if it also contains commas/quotes/newlines (e.g.
 * `=SUM(A1,A2)` → `'=SUM(A1,A2)` → `"'=SUM(A1,A2)"`).
 */
export function escapeCsvCell(value: string): string {
  const safe = neutraliseFormulaInjection(value);
  if (safe === "") return "";
  if (/[",\r\n]/.test(safe)) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
}

/**
 * Normalise any extracted cell value into its string form. Mirrors the shape
 * that text writers expect: null/undefined → empty, booleans → true/false,
 * everything else via String().
 */
export function stringifyCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value);
}
